<xemex-dialog-shell
  [title]="'installations.editor.binding.title' | translate"
  [subtitle]="
    (data.mode === 'bind'
      ? 'installations.editor.binding.subtitleBind'
      : 'installations.editor.binding.subtitleChange'
    ) | translate
  "
>
  <div class="binding-dialog">
    @if (point; as shownPoint) {
      <section class="point-card" data-testid="point-card">
        <span class="point-card__icon">
          <mat-icon>{{ categoryIcon }}</mat-icon>
        </span>
        <div class="point-card__name">
          <span class="binding-dialog__overline binding-dialog__overline--accent">
            {{ 'installations.editor.binding.point' | translate }}
          </span>
          <span class="point-card__title">
            <span class="point-card__point">{{ shownPoint.name }}</span>
            @if (shownPoint.required) {
              <xemex-tag
                class="point-card__required"
                state="success"
                [label]="'installations.editor.binding.required' | translate"
              />
            }
          </span>
        </div>
        <dl class="point-card__facts">
          <div>
            <dt>{{ 'installations.editor.binding.category' | translate }}</dt>
            <dd>{{ 'installations.roleGroups.' + category | translate }}</dd>
          </div>
          <div>
            <dt>{{ 'installations.dataSources.columns.unit' | translate }}</dt>
            <dd>{{ shownPoint.unit }}</dd>
          </div>
          <div>
            <dt>{{ 'installations.dataSources.columns.kind' | translate }}</dt>
            <dd>{{ 'installations.nature.' + shownPoint.nature | translate }}</dd>
          </div>
        </dl>
      </section>
    }

    @if (isLoading() && !candidates()) {
      <xemex-spinner />
    } @else if (loadFailed()) {
      <div class="flex items-center justify-between gap-4">
        <span>{{ 'installations.editor.binding.candidatesFailed' | translate }}</span>
        <xemex-text-button icon="refresh" (click)="load()">
          {{ 'installations.insights.retry' | translate }}
        </xemex-text-button>
      </div>
    } @else if (candidates()) {
      <div class="change">
        <section class="channel-card channel-card--current" data-testid="current-card">
          <span class="binding-dialog__overline">
            {{ 'installations.editor.binding.current' | translate }}
          </span>
          @if (currentCard(); as card) {
            <ng-container
              [ngTemplateOutlet]="cardBody"
              [ngTemplateOutletContext]="{ $implicit: card }"
            />
            <span class="channel-card__line">
              {{ 'installations.editor.binding.linkedSince' | translate: { date: linkedSince } }}
            </span>
          } @else {
            <span class="channel-card__empty">
              {{ 'installations.dataSources.state.unbound' | translate }}
            </span>
          }
        </section>
        <mat-icon class="change__arrow" aria-hidden="true">arrow_forward</mat-icon>
        <section class="channel-card channel-card--new" data-testid="new-card">
          <span class="binding-dialog__overline">
            {{ 'installations.editor.binding.new' | translate }}
          </span>
          @if (newCard(); as card) {
            <ng-container
              [ngTemplateOutlet]="cardBody"
              [ngTemplateOutletContext]="{ $implicit: card }"
            />
          } @else {
            <span class="channel-card__empty">
              {{ 'installations.editor.binding.selectChannel' | translate }}
            </span>
          }
        </section>
      </div>

      @if (hasCandidates()) {
        <xemex-effective-from-field
          [(instant)]="instant"
          [timeZone]="data.timeZone"
          [error]="messageAt('instant')"
          [hint]="
            startedAt
              ? ('installations.editor.effectiveFrom.hintChange' | translate: { date: startedAt })
              : ('installations.editor.effectiveFrom.hintBind' | translate)
          "
          [ruleBroken]="instantBroken()"
        />
      } @else {
        <p class="binding-dialog__note" data-testid="binding-note">
          {{ 'installations.editor.binding.note.none' | translate }}
          {{
            deviceNames
              ? ('installations.editor.binding.devicesHere' | translate: { devices: deviceNames })
              : ('installations.editor.binding.noDevicesHere' | translate)
          }}
        </p>
      }

      @if (devices().length) {
        <section class="binding-dialog__section">
          <h3 class="binding-dialog__heading">
            {{ 'installations.editor.binding.newSource' | translate }}
          </h3>
          <div class="device-tiles">
            @for (entry of devices(); track entry.device.deviceId) {
              <button
                type="button"
                class="device-tile"
                data-testid="device-tile"
                [class.device-tile--shown]="entry === shownDevice()"
                [attr.aria-pressed]="entry === shownDevice()"
                (click)="showDevice(entry.device.deviceId)"
              >
                <mat-icon class="device-tile__icon" aria-hidden="true">memory</mat-icon>
                <span class="device-tile__text">
                  <span class="device-tile__name">
                    {{ entry.device.deviceTypeName ?? entry.device.protocol ?? entry.device.ldn }}
                  </span>
                  <span class="device-tile__id">
                    {{ entry.device.ldn ?? entry.device.serial ?? entry.device.deviceId }}
                  </span>
                </span>
                <span class="device-tile__count">
                  {{
                    'installations.editor.binding.available' | translate: { count: entry.available }
                  }}
                </span>
              </button>
            }
          </div>
        </section>

        <section class="binding-dialog__section">
          <div class="binding-dialog__section-header">
            <h3 class="binding-dialog__heading">
              {{ 'installations.editor.binding.channels' | translate }}
            </h3>
            @if (point) {
              <span class="binding-dialog__hint">
                {{ 'installations.editor.binding.onlyUnit' | translate: { unit: point.unit } }}
              </span>
            }
          </div>
          @if (note(); as shown) {
            @if (shown !== 'none') {
              <p class="binding-dialog__note" data-testid="binding-note">
                {{ 'installations.editor.binding.note.' + shown | translate }}
              </p>
            }
          }
          @if (messageAt('candidates'); as messageKey) {
            <p class="binding-dialog__error" role="alert">{{ messageKey | translate }}</p>
          }
          <mat-radio-group
            class="channels"
            [attr.aria-label]="'installations.editor.binding.channels' | translate"
            [value]="selectedKey()"
            (change)="select($event.value)"
          >
            <table class="channels__table">
              <thead>
                <tr>
                  <th class="channels__radio"></th>
                  <th>{{ 'installations.editor.binding.columns.channel' | translate }}</th>
                  <th class="channels__unit">
                    {{ 'installations.dataSources.columns.unit' | translate }}
                  </th>
                  <th>{{ 'installations.dataSources.columns.status' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (shown of shownRows(); track shown.row.key) {
                  @let row = shown.row;
                  <tr
                    class="channels__row"
                    data-testid="channel-row"
                    [class.channels__row--disabled]="!row.selectable"
                    [class.channels__row--selected]="row.key === selectedKey()"
                    (click)="row.selectable && select(row.key)"
                  >
                    <td class="channels__radio">
                      <mat-radio-button
                        [value]="row.key"
                        [disabled]="!row.selectable"
                        [aria-label]="
                          row.context
                            ? row.source.valueName + ' · ' + row.context
                            : (row.source.valueName ?? row.key)
                        "
                      />
                    </td>
                    <td class="channels__name">
                      <span>{{ row.source.valueName }}</span>
                      @if (row.context) {
                        <span class="channels__context">{{ row.context }}</span>
                      }
                    </td>
                    <td class="channels__unit">{{ row.unit ?? '–' }}</td>
                    <td class="channels__status">
                      <xemex-tag
                        class="channels__tag"
                        [state]="shown.tag.state"
                        [icon]="shown.tag.icon ?? undefined"
                        [label]="shown.tag.key | translate: shown.tag.params"
                        [attr.title]="shown.tag.key | translate: shown.tag.params"
                      />
                      @if (row.sharedWith.length) {
                        <span class="channels__extra">
                          {{
                            'installations.editor.candidate.sharedWith'
                              | translate: { points: row.sharedWith.join(', ') }
                          }}
                        </span>
                      }
                      @if (row.otherInstallations.length) {
                        <span class="channels__extra" data-testid="also-feeds">
                          {{
                            'installations.editor.candidate.alsoFeeds'
                              | translate: { installations: row.otherInstallations.join(', ') }
                          }}
                        </span>
                      }
                      @if (candidateError(row.key); as messageKey) {
                        <span class="binding-dialog__error" role="alert">{{
                          messageKey | translate
                        }}</span>
                      }
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td class="channels__empty" colspan="4">
                      {{ 'installations.editor.binding.noChannels' | translate }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </mat-radio-group>
        </section>
      }
    }

    @if (messageAt('dialog'); as messageKey) {
      <p class="binding-dialog__error" role="alert">{{ messageKey | translate }}</p>
    }
  </div>

  <div actions class="binding-dialog__actions">
    @if (hasCandidates()) {
      <span class="binding-dialog__footer-hint">
        {{ 'installations.editor.binding.footer' | translate }}
      </span>
    }
    <xemex-text-button (click)="cancel()">
      {{ 'general.btnCancel' | translate }}
    </xemex-text-button>
    @if (hasCandidates()) {
      <xemex-filled-button [disabled]="!canSave()" [loading]="isSaving()" (click)="save()">
        {{
          (data.mode === 'bind'
            ? 'installations.editor.binding.bind'
            : 'installations.editor.binding.scheduleChange'
          ) | translate
        }}
      </xemex-filled-button>
    }
  </div>
</xemex-dialog-shell>

<ng-template #cardBody let-card>
  <span class="channel-card__channel">
    <span class="channel-card__name">{{ card.name }}</span>
    @if (card.unit) {
      <span class="channel-card__unit">{{ card.unit }}</span>
    }
  </span>
  <span class="channel-card__line">{{ card.device }}</span>
</ng-template>
